import { RolesAssessmentTeamEnum, getValuesRadioButton } from "@shape/reef-shared";
import type { AssessmentTeamCreate } from "@shape/reef-shared";
import type {
  CreateStep2MdbBody,
  CreateStep2MdbFormValues,
  CreateStep2RiskAssessmentComplementBody,
} from "types";

const parseAssessmentTeam = (formValues: CreateStep2MdbFormValues): AssessmentTeamCreate[] => {
  const assessmentTeam = (formValues.assessment_team || []) as Array<
    AssessmentTeamCreate & { user?: { user_id?: number } }
  >;
  const assessmentTeamPlus: AssessmentTeamCreate[] = (formValues.assessment_team_plus || []).map(
    (userId) => ({
      user_id: Number(userId),
      role_code: RolesAssessmentTeamEnum.OTHER,
    })
  );

  const assessmentTeamValid = assessmentTeam
    .map(({ role_code, user, user_id }) => ({
      user_id: user_id ?? user?.user_id ?? 0,
      role_code,
    }))
    .filter(({ user_id }) => user_id);

  return [...assessmentTeamValid, ...assessmentTeamPlus];
};

const parseAssigningRecommendations = (
  assigningRecommendations: CreateStep2MdbFormValues["assigning_recommendations"]
): CreateStep2MdbBody["assigning_recommendations"] =>
  assigningRecommendations.map(
    ({ has_action_to_be_closed, permanent_change, tags, temporary_due_date, ...rest }) => ({
      ...rest,
      tag_codes: tags ?? null,
      permanent_change,
      has_action_to_be_closed: permanent_change ? null : has_action_to_be_closed ?? null,
      temporary_due_date:
        has_action_to_be_closed === null || has_action_to_be_closed
          ? null
          : temporary_due_date ?? null,
    })
  );

export const buildStep2SmeAssessmentBody = (
  formValues: CreateStep2MdbFormValues
): CreateStep2MdbBody => {
  const rest = { ...formValues } as CreateStep2MdbFormValues & Record<string, unknown>;
  delete rest.assessment_team_plus;
  delete rest.cto_expiry_date;
  delete rest.risk_assessment_decisions;
  delete rest.risk_assessment_status;
  delete rest.recommendation_tags;
  delete rest.external_element_impacted;

  return {
    ...(rest as unknown as CreateStep2MdbBody),
    potential_consequences_affected: formValues.potential_consequences_affected,
    assessment_team: parseAssessmentTeam(formValues),
    mopo_reviewed: getValuesRadioButton(formValues.mopo_reviewed),
    is_issue_managed_in_other_system: getValuesRadioButton(
      formValues.is_issue_managed_in_other_system ?? null
    ),
    contingency_plan: null,
    assigning_recommendations: parseAssigningRecommendations(formValues.assigning_recommendations),
  };
};

export const buildStep2RiskAssessmentComplementBody = (
  formValues: CreateStep2MdbFormValues
): CreateStep2RiskAssessmentComplementBody => ({
  risk_level: formValues.risk_level,
  risk_level_justification: formValues.risk_level_justification,
  risk_level_files: formValues.risk_level_files,
  risk_level_after_measures: formValues.risk_level_after_measures,
  risk_level_after_measures_comment: formValues.risk_level_after_measures_comment,
  risk_level_after_measures_files: formValues.risk_level_after_measures_files,
  assigning_recommendations: parseAssigningRecommendations(formValues.assigning_recommendations),
  draft: formValues.draft,
});
